import React from 'react';
import { Alert, Button, Modal, Popover, Switch, Table, Tag, Typography } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { AzureScope } from '../../../../services/azure/azureOnboarding/azureOnboarding.model';
import {
  agreementLabel,
  formatDateTime,
  isCspScope,
  isSubscription,
  scopeStatusColor,
  scopeTypeLabel,
} from '../azureOnboarding.utils';

const { Text, Paragraph } = Typography;

interface ScopesModalProps {
  title: string;
  scopes: AzureScope[];
  loading: boolean;
  canUpdate: boolean;
  togglingId: number | null;
  onToggle: (scope: AzureScope, active: boolean) => Promise<void>;
  onClose: () => void;
}

const ScopesModal: React.FC<ScopesModalProps> = (props) => {
  const { title, scopes, loading, canUpdate, togglingId, onToggle, onClose } = props;
  const hasCsp = scopes.some(isCspScope);

  const confirmToggle = (scope: AzureScope, active: boolean) => {
    const name = scope.display_name || scope.scope_id;
    if (!active) {
      Modal.confirm({
        title: `Switch off ${name}?`,
        icon: <ExclamationCircleOutlined />,
        content: 'Collection for this scope stops. Rediscover never switches it back on.',
        okText: 'Switch off',
        cancelText: 'Cancel',
        centered: true,
        onOk: () => onToggle(scope, false),
      });
    } else if (isSubscription(scope) && !isCspScope(scope)) {
      Modal.confirm({
        title: `Switch on ${name}?`,
        icon: <ExclamationCircleOutlined />,
        content: (
          <>
            Check the Azure Usage Detail column mapping handles the legacy (subscription-scope)
            schema before switching this on. Switching it on doesn&apos;t grant access by itself:
            the client must give the <strong>Synapse 360 Azure</strong> app{' '}
            <strong>Cost Management Reader</strong> on this subscription. Then run Verify.
          </>
        ),
        okText: 'Switch on',
        cancelText: 'Cancel',
        centered: true,
        width: 520,
        onOk: () => onToggle(scope, true),
      });
    } else {
      onToggle(scope, true);
    }
  };

  const columns: ColumnsType<AzureScope> = [
    {
      title: 'Type',
      dataIndex: 'scope_type',
      key: 'scope_type',
      render: (val: string) => scopeTypeLabel(val),
    },
    {
      title: 'Scope id',
      dataIndex: 'scope_id',
      key: 'scope_id',
      render: (val: string) => (
        <Text code copyable style={{ wordBreak: 'break-all' }}>
          {val}
        </Text>
      ),
    },
    {
      title: 'Display name',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (val: string | null) => val || '–',
    },
    {
      title: 'Agreement type',
      key: 'agreement_type',
      render: (_, record) => (
        <>
          {agreementLabel(record.agreement_type)}{' '}
          {isCspScope(record) && <Tag color="orange">CSP</Tag>}
        </>
      ),
    },
    {
      title: 'Active',
      key: 'active',
      render: (_, record) => (
        <Switch
          checked={record.active}
          disabled={!canUpdate}
          loading={togglingId === record.id}
          onChange={(checked) => confirmToggle(record, checked)}
          aria-label={`Active ${record.display_name || record.scope_id}`}
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (val: string, record) =>
        record.active ? (
          <Tag color={scopeStatusColor[val] || 'default'}>{val}</Tag>
        ) : (
          <Tag style={{ opacity: 0.6 }}>{val}</Tag>
        ),
    },
    {
      title: 'Last verified',
      dataIndex: 'last_verified_date',
      key: 'last_verified_date',
      render: (val: string | null) => formatDateTime(val),
    },
    {
      title: 'Last error',
      dataIndex: 'last_error',
      key: 'last_error',
      width: 200,
      render: (val: string | null) =>
        val ? (
          <Popover
            content={
              <pre style={{ maxWidth: 480, maxHeight: 300, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                {val}
              </pre>
            }
            trigger="click"
          >
            <Text type="danger" ellipsis style={{ maxWidth: 200, cursor: 'pointer' }}>
              {val}
            </Text>
          </Popover>
        ) : (
          '–'
        ),
    },
  ];

  return (
    <Modal
      wrapClassName="custom-modal"
      title={title}
      centered
      visible
      onCancel={onClose}
      footer={null}
      width={1200}
    >
      <Paragraph type="secondary">
        New billing accounts and CSP subscriptions start <strong>on</strong>. Other subscriptions
        that aren&apos;t billed under a visible billing account (pay-as-you-go, or EA/MCA whose
        billing account isn&apos;t visible) start <strong>off</strong> on purpose. Usage Detail at
        subscription scope returns Azure&apos;s <em>legacy</em> schema for them, which the Azure
        Usage Detail column mapping isn&apos;t built for. They may also turn out to be billed under
        a billing account that becomes visible later, and collecting both would double-count.
      </Paragraph>
      {hasCsp && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <>
              CSP subscriptions: the client&apos;s CSP partner must turn on{' '}
              <strong>cost visibility</strong> for this customer (partner&apos;s Azure portal →
              Cost Management + Billing → Customers → the customer → Policies). It&apos;s off by
              default, and until it&apos;s on, no cost data is visible. Costs show at retail
              (pay-as-you-go) rates, not the partner&apos;s prices.
            </>
          }
        />
      )}
      <Table<AzureScope>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={scopes}
        loading={loading}
        pagination={false}
        scroll={{ x: true }}
        locale={{ emptyText: 'No billing accounts or subscriptions discovered yet.' }}
      />
      <div className="btns-block modal-footer">
        <Button type="primary" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default ScopesModal;
